import { DEFAULT_STATUSES } from '../../FlowchartView/TaskNode/constants';
import { calculateProgress } from '../../FlowchartView/utils/statusUtils';
import {
  getNestDependencyInfo,
  isCompressibleTrailSegment,
} from '../../shared/nestDependencyInfo';
import { replaceTaskInConditions } from '../../shared/ConditionsDropdown/utils/conditionHelpers';
import { generateTaskId } from '../taskActions/utils';
import { findConnectedNodes } from '../../FlowchartView/hooks/utils/graphTraversal';
import {
  inheritTagDefinitionsFromParent,
  collectExternalDependencyIdsForWrapper,
  externalDependencyIdsToDependsOn,
  replaceDependsOnIdsWithWrapper,
  buildNestedPreparedTaskForCompile,
  applyTidyLayoutToCompiledTasks,
} from './nestTaskHelpers';

/**
 * Create a new mission whose nested campaign contains the selected missions (flow bulk action).
 * Allowed when: (a) no cross-boundary deps, or (b) selection is a compressible trail (convex segment).
 */
export function runCompileMissionsIntoNestedCampaign({
  missionName,
  draggedIds,
  tasks,
  nestedSprints,
  allSprints = [],
  localSprint,
  setLocalSprint,
  onAddSprint,
  onUpdateSprint,
  recordAction,
  onTrashSprint,
  flowchartRef,
}) {
  const trimmed = (missionName || '').trim();
  if (!trimmed || !Array.isArray(draggedIds) || draggedIds.length < 2) return;

  const draggedSet = new Set(draggedIds);
  const depInfo = getNestDependencyInfo(tasks, draggedSet);
  const noExternalDeps = depInfo.upstreamCount === 0 && depInfo.downstreamCount === 0;
  if (!noExternalDeps && !isCompressibleTrailSegment(tasks, draggedSet)) return;

  const tasksToMove = [...draggedIds];
  const movingTasks = tasks.filter((t) => tasksToMove.includes(t.id));
  if (movingTasks.length === 0) return;

  const originalTasks = tasks.map((t) => ({ ...t }));

  const newTaskId = generateTaskId();
  const nestedSprintId = `nested-sprint-${Date.now()}`;
  const finalNestedSprintId = nestedSprintId;

  const sprintName = trimmed.length > 35 ? `${trimmed.slice(0, 35)}...` : trimmed;

  const inheritedTagDefs = inheritTagDefinitionsFromParent(localSprint);
  const nestedSprint = {
    id: nestedSprintId,
    name: sprintName,
    ownerId: localSprint.ownerId,
    ownerName: localSprint.ownerName,
    status: 'active',
    progress: 0,
    createdAt: new Date().toISOString(),
    parentTaskId: newTaskId,
    parentSprintId: localSprint.id,
    tasks: [],
    categories: inheritedTagDefs.length ? inheritedTagDefs : [],
    ...(inheritedTagDefs.length && localSprint.tags?.length
      ? { tags: inheritedTagDefs.map((t) => ({ ...t })) }
      : {}),
    statuses: localSprint.statuses || DEFAULT_STATUSES,
  };

  const anchorTask = movingTasks.reduce((anchor, task) => {
    const taskX = task.x ?? 0;
    const taskY = task.y ?? 0;
    const anchorX = anchor.x ?? 0;
    const anchorY = anchor.y ?? 0;
    if (taskY < anchorY || (taskY === anchorY && taskX < anchorX)) return task;
    return anchor;
  }, movingTasks[0]);

  const anchorX = anchorTask.x ?? 0;
  const anchorY = anchorTask.y ?? 0;
  const destAnchorX = 100;
  const destAnchorY = 100;

  const movingSet = new Set(tasksToMove);
  const wrapperExternalIds = collectExternalDependencyIdsForWrapper(movingTasks, movingSet);
  const wrapperDependsOn = externalDependencyIdsToDependsOn(wrapperExternalIds, movingTasks[0]);

  const preparedTasks = movingTasks.map((task) =>
    buildNestedPreparedTaskForCompile(task, movingSet, anchorX, anchorY, destAnchorX, destAnchorY),
  );

  const tidiedPreparedTasks = applyTidyLayoutToCompiledTasks(preparedTasks);

  let updatedNestedSprint = {
    ...nestedSprint,
    tasks: [...(nestedSprint.tasks || []), ...tidiedPreparedTasks],
    categories: nestedSprint.categories || [],
  };

  const movingTaskNestedSprintIds = movingTasks.filter((t) => t.nestedSprintId).map((t) => t.nestedSprintId);

  const allSprintsForProgress = (allSprints || []).map((s) =>
    movingTaskNestedSprintIds.includes(s.id) ? { ...s, parentSprintId: finalNestedSprintId } : s,
  );
  const nestedSprintIdsInCompiledTasks = new Set(
    tidiedPreparedTasks.map((t) => t.nestedSprintId).filter(Boolean),
  );
  const nestedSprintsForProgress = [...nestedSprintIdsInCompiledTasks]
    .map((id) => {
      const s = nestedSprints.find((ns) => ns.id === id);
      return s ? { ...s, parentSprintId: finalNestedSprintId } : null;
    })
    .filter(Boolean);
  const initialCompiledProgress = calculateProgress(
    updatedNestedSprint.tasks,
    updatedNestedSprint.statuses || DEFAULT_STATUSES,
    nestedSprintsForProgress,
    allSprintsForProgress,
  );
  updatedNestedSprint = { ...updatedNestedSprint, progress: initialCompiledProgress };

  const originalChildSprintParents = {};
  movingTaskNestedSprintIds.forEach((childSprintId) => {
    const childSprint = nestedSprints.find((ns) => ns.id === childSprintId);
    if (childSprint) {
      originalChildSprintParents[childSprintId] = childSprint.parentSprintId;
    }
  });

  if (movingTaskNestedSprintIds.length > 0 && onUpdateSprint) {
    movingTaskNestedSprintIds.forEach((childSprintId) => {
      const childSprint = nestedSprints.find((ns) => ns.id === childSprintId);
      if (childSprint) {
        onUpdateSprint({
          ...childSprint,
          tasks: childSprint.tasks || [],
          categories: childSprint.categories || [],
          parentSprintId: finalNestedSprintId,
        });
      }
    });
  }

  const patchedCurrentTasks = tasks.map((task) => {
    if (movingSet.has(task.id)) return task;
    if (noExternalDeps) {
      const deps = Array.isArray(task.dependsOn) ? task.dependsOn : [];
      let hasMovingDep = false;
      const patchedDeps = [];
      deps.forEach((dep) => {
        const depId = typeof dep === 'string' ? dep : dep?.taskId;
        if (movingSet.has(depId)) {
          hasMovingDep = true;
          const movingTask = tasks.find((t) => t.id === depId);
          (Array.isArray(movingTask?.dependsOn) ? movingTask.dependsOn : []).forEach((upstreamDep) => {
            const upstreamId = typeof upstreamDep === 'string' ? upstreamDep : upstreamDep?.taskId;
            if (!movingSet.has(upstreamId)) {
              patchedDeps.push(upstreamDep);
            }
          });
        } else {
          patchedDeps.push(dep);
        }
      });
      if (hasMovingDep) {
        return { ...task, dependsOn: patchedDeps };
      }
      return task;
    }
    let patched = { ...task };
    if (Array.isArray(patched.dependsOn)) {
      patched.dependsOn = replaceDependsOnIdsWithWrapper(patched.dependsOn, movingSet, newTaskId);
    }
    if (patched.unlockConditions) {
      let uc = patched.unlockConditions;
      for (const sid of movingSet) {
        uc = replaceTaskInConditions(uc, sid, newTaskId);
      }
      patched.unlockConditions = uc;
    }
    return patched;
  });

  const NODE_WIDTH = 288;
  const NODE_HEIGHT = 150;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const task of movingTasks) {
    const x = task.x ?? 0;
    const y = task.y ?? 0;
    const w = task.width || NODE_WIDTH;
    const h = task.height || NODE_HEIGHT;
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x + w);
    maxY = Math.max(maxY, y + h);
  }
  if (!Number.isFinite(minX)) {
    minX = 100;
    minY = 100;
    maxX = 400;
    maxY = 250;
  }
  const parentX = (minX + maxX) / 2 - NODE_WIDTH / 2;
  const parentY = (minY + maxY) / 2 - NODE_HEIGHT / 2;

  const now = new Date().toISOString();
  const newParentTask = {
    id: newTaskId,
    name: trimmed,
    order: (localSprint?.tasks?.length || 0) + 1,
    groupId: null,
    dependsOn: noExternalDeps ? [] : wrapperDependsOn,
    status: 'pending',
    x: parentX,
    y: parentY,
    nestedSprintId: finalNestedSprintId,
    createdAt: now,
    updatedAt: now,
    startedAt: null,
    completedAt: null,
    estimatedDurationMinutes: null,
    dueDate: null,
  };

  const remainingTasks = patchedCurrentTasks.filter((t) => !tasksToMove.includes(t.id)).concat([newParentTask]);

  const updatedParentSprint = localSprint
    ? { ...localSprint, tasks: remainingTasks }
    : null;

  setLocalSprint((prev) => {
    if (!prev) return prev;
    return {
      ...prev,
      tasks: remainingTasks,
    };
  });

  if (updatedParentSprint && onUpdateSprint) {
    onUpdateSprint(updatedParentSprint);
  }

  if (onAddSprint) {
    onAddSprint(updatedNestedSprint);
  }

  const connectedBeforeCompile = findConnectedNodes(new Set(draggedIds), tasks);
  const parentTidySeedIds = new Set([newTaskId]);
  connectedBeforeCompile.forEach((id) => {
    if (!movingSet.has(id)) parentTidySeedIds.add(id);
  });
  const connectedAfterCompile = findConnectedNodes(parentTidySeedIds, remainingTasks);
  flowchartRef?.current?.clearCustomPositionsForNodeIds?.(connectedAfterCompile);
  if (connectedAfterCompile.size > 0) {
    setTimeout(() => {
      flowchartRef?.current?.autoLayout?.(connectedAfterCompile, { skipRecordAction: true });
    }, 50);
  }

  if (recordAction) {
    const description = `Compile into nested: "${trimmed}"`;
    recordAction({
      type: 'NEST_TASKS',
      description,
      undo: () => {
        setLocalSprint((prev) => {
          if (!prev) return prev;
          const restoredTasks = originalTasks.map((origTask) => ({ ...origTask }));
          return {
            ...prev,
            tasks: restoredTasks,
          };
        });
        if (onTrashSprint) {
          onTrashSprint(finalNestedSprintId);
        }
        if (Object.keys(originalChildSprintParents).length > 0 && onUpdateSprint) {
          Object.entries(originalChildSprintParents).forEach(([childSprintId, originalParentId]) => {
            const childSprint = nestedSprints.find((ns) => ns.id === childSprintId);
            if (childSprint) {
              onUpdateSprint({
                ...childSprint,
                tasks: childSprint.tasks || [],
                categories: childSprint.categories || [],
                parentSprintId: originalParentId,
              });
            }
          });
        }
      },
      redo: () => {
        setLocalSprint((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            tasks: remainingTasks,
          };
        });
        if (onAddSprint) {
          onAddSprint(updatedNestedSprint);
        }
        if (movingTaskNestedSprintIds.length > 0 && onUpdateSprint) {
          movingTaskNestedSprintIds.forEach((childSprintId) => {
            const childSprint = nestedSprints.find((ns) => ns.id === childSprintId);
            if (childSprint) {
              onUpdateSprint({
                ...childSprint,
                tasks: childSprint.tasks || [],
                categories: childSprint.categories || [],
                parentSprintId: finalNestedSprintId,
              });
            }
          });
        }
      },
    });
  }
}
